<script setup>
import { onMounted, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import {
  useStore,
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { RFM_SEGMENT_MAP } from 'rfm/constants/segments';
import { segmentLabel } from 'rfm/constants/segmentLabels';

import ContactsDetailsLayout from 'dashboard/components-next/Contacts/ContactsDetailsLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Accordion from 'dashboard/components-next/Accordion/Accordion.vue';
import OverviewHighlightsCard from 'dashboard/components-next/Contacts/ContactsSidebar/components/OverviewHighlightsCard.vue';
import OverviewNotesCard from 'dashboard/components-next/Contacts/ContactsSidebar/components/OverviewNotesCard.vue';
import OverviewTasksCard from 'dashboard/components-next/Contacts/ContactsSidebar/components/OverviewTasksCard.vue';
import ContactDetails from 'dashboard/components-next/Contacts/Pages/ContactDetails.vue';
import ContactNotes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactNotes.vue';
import ContactActivities from 'dashboard/components-next/Contacts/ContactsSidebar/ContactActivities.vue';
import ContactHistory from 'dashboard/components-next/Contacts/ContactsSidebar/ContactHistory.vue';
import ContactMerge from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMerge.vue';
import ContactCustomAttributes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactCustomAttributes.vue';
import ContactEcommerceOrders from 'dashboard/components-next/Contacts/ContactsSidebar/ContactEcommerceOrders.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();

const contact = useMapGetter('contacts/getContactById');
const uiFlags = useMapGetter('contacts/getUIFlags');

const ecommerceIntegrations = ['shopify', 'nuvemshop'].map(id =>
  useFunctionGetter('integrations/getIntegration', id)
);
const isEcommerceEnabled = computed(() =>
  ecommerceIntegrations.some(i => i.value.enabled)
);

const activeTab = ref('overview');
const contactMergeRef = ref(null);
const avatarUrl = ref('');

const isFetchingItem = computed(() => uiFlags.value.isFetchingItem);
const isMergingContact = computed(() => uiFlags.value.isMerging);
const isUpdatingContact = computed(() => uiFlags.value.isUpdating);

const selectedContact = computed(() => contact.value(route.params.contactId));

const { isFeatureFlagEnabled } = usePolicy();
const { t } = useI18n();

const rfmSegment = computed(() => {
  const key = selectedContact.value?.rfmSegment;
  if (!key) return null;
  return { ...RFM_SEGMENT_MAP[key], label: segmentLabel(t, key) };
});

const showRfm = computed(
  () => isFeatureFlagEnabled(FEATURE_FLAGS.RFM) && !!rfmSegment.value
);

const showKanban = computed(() => isFeatureFlagEnabled(FEATURE_FLAGS.KANBAN));

const avatarSrc = computed(
  () => avatarUrl.value || selectedContact.value?.thumbnail || ''
);

const showSpinner = computed(
  () => isFetchingItem.value || isMergingContact.value
);

const BASE_TABS = [
  { key: 'OVERVIEW', value: 'overview' },
  { key: 'ACTIVITIES', value: 'activities' },
  { key: 'CONVERSATIONS', value: 'history' },
  { key: 'NOTES', value: 'notes' },
  { key: 'ATTRIBUTES', value: 'attributes' },
  { key: 'MERGE', value: 'merge' },
];

const CONTACT_TABS_OPTIONS = computed(() => {
  const tabs = [...BASE_TABS];
  if (isEcommerceEnabled.value) {
    tabs.splice(tabs.length - 1, 0, {
      key: 'ECOMMERCE_ORDERS',
      value: 'ecommerce_orders',
    });
  }
  return tabs;
});

const tabs = computed(() => {
  return CONTACT_TABS_OPTIONS.value.map(tab => ({
    label: t(`CONTACTS_LAYOUT.SIDEBAR.TABS.${tab.key}`),
    value: tab.value,
  }));
});

const activeTabIndex = computed(() => {
  return CONTACT_TABS_OPTIONS.value.findIndex(v => v.value === activeTab.value);
});

const goToContactsList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/contacts?page=1`);
  }
};

const fetchActiveContact = async () => {
  if (route.params.contactId) {
    await store.dispatch('contacts/show', { id: route.params.contactId });
    await store.dispatch(
      'contacts/fetchContactableInbox',
      route.params.contactId
    );
  }
};

const handleTabChange = tab => {
  activeTab.value = tab.value;
};

const goToTab = value => {
  activeTab.value = value;
};

const onTabChange = index => {
  activeTab.value = CONTACT_TABS_OPTIONS.value[index]?.value;
};

const handleAvatarUpload = async ({ file, url }) => {
  avatarUrl.value = url;
  try {
    await store.dispatch('contacts/update', {
      id: selectedContact.value.id,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE'));
  }
};

const handleAvatarDelete = async () => {
  try {
    await store.dispatch('contacts/deleteAvatar', selectedContact.value.id);
    avatarUrl.value = '';
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error.message
        ? error.message
        : t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE')
    );
  }
};

const fetchContactNotes = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactNotes/get', { contactId });
};

const fetchContactConversations = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactConversations/get', contactId);
};

const fetchAttributes = () => {
  store.dispatch('attributes/get');
};

const toggleContactBlock = async isBlocked => {
  const ALERT_MESSAGES = {
    success: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_SUCCESS_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_SUCCESS_MESSAGE'),
    },
    error: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_ERROR_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_ERROR_MESSAGE'),
    },
  };

  try {
    await store.dispatch(`contacts/update`, {
      ...selectedContact.value,
      blocked: !isBlocked,
    });
    useAlert(
      isBlocked ? ALERT_MESSAGES.success.unblock : ALERT_MESSAGES.success.block
    );
  } catch (error) {
    useAlert(
      isBlocked ? ALERT_MESSAGES.error.unblock : ALERT_MESSAGES.error.block
    );
  }
};

onMounted(() => {
  fetchActiveContact();
  fetchContactNotes();
  fetchContactConversations();
  fetchAttributes();
  store.dispatch('integrations/get');
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-surface-1"
  >
    <ContactsDetailsLayout
      :button-label="$t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE')"
      :selected-contact="selectedContact"
      :avatar-src="avatarSrc"
      is-detail-view
      :show-pagination-footer="false"
      :is-updating="isUpdatingContact"
      @go-to-contacts-list="goToContactsList"
      @toggle-block="toggleContactBlock"
      @upload-avatar="handleAvatarUpload"
      @delete-avatar="handleAvatarDelete"
    >
      <div
        v-if="showSpinner"
        class="flex items-center justify-center flex-1 py-10 text-n-slate-11"
      >
        <Spinner />
      </div>
      <div
        v-else-if="selectedContact"
        class="flex flex-col flex-1 w-full overflow-hidden lg:flex-row"
      >
        <!-- Left - main area with tabs -->
        <div class="flex flex-col flex-1 min-w-0 overflow-hidden">
          <woot-tabs
            :index="activeTabIndex"
            class="px-4 shrink-0 bg-n-surface-1"
            @change="onTabChange"
          >
            <woot-tabs-item
              v-for="(tab, index) in tabs"
              :key="tab.value"
              :index="index"
              :name="tab.label"
              :show-badge="false"
              is-compact
            />
          </woot-tabs>
          <div
            v-if="isFetchingItem"
            class="flex items-center justify-center flex-1 text-n-slate-11"
          >
            <Spinner />
          </div>
          <ContactNotes
            v-else-if="activeTab === 'notes'"
            class="flex-1 min-h-0"
          />
          <div v-else class="flex-1 min-h-0 p-4 overflow-y-auto">
            <div v-if="activeTab === 'overview'" class="flex flex-col gap-4">
              <Accordion
                :title="t('CONTACTS_LAYOUT.DETAILS.SECTIONS.HIGHLIGHTS')"
                is-open
              >
                <OverviewHighlightsCard
                  :selected-contact="selectedContact"
                  :rfm-segment="rfmSegment"
                  :show-rfm="showRfm"
                />
              </Accordion>
              <Accordion
                :title="t('CONTACTS_LAYOUT.DETAILS.SECTIONS.ACTIVITY')"
                is-open
              >
                <ContactActivities preview @view-all="goToTab('activities')" />
              </Accordion>
              <Accordion
                :title="t('CONTACTS_LAYOUT.SIDEBAR.TABS.NOTES')"
                is-open
              >
                <OverviewNotesCard @view-all="goToTab('notes')" />
              </Accordion>
              <Accordion
                v-if="showKanban"
                :title="t('CONTACTS_LAYOUT.DETAILS.SECTIONS.TASKS')"
                is-open
              >
                <OverviewTasksCard />
              </Accordion>
            </div>
            <ContactActivities v-else-if="activeTab === 'activities'" />
            <ContactHistory v-else-if="activeTab === 'history'" />
            <ContactCustomAttributes
              v-else-if="activeTab === 'attributes'"
              :selected-contact="selectedContact"
            />
            <ContactEcommerceOrders
              v-else-if="activeTab === 'ecommerce_orders'"
              :contact-id="route.params.contactId"
            />
            <ContactMerge
              v-else-if="activeTab === 'merge'"
              ref="contactMergeRef"
              :selected-contact="selectedContact"
              @go-to-contacts-list="goToContactsList"
              @reset-tab="handleTabChange(BASE_TABS[0])"
            />
          </div>
        </div>

        <!-- Right - details sidebar with its own tabs -->
        <aside
          class="flex flex-col w-full overflow-y-auto border-t lg:border-t-0 lg:border-l shrink-0 lg:w-96 border-n-weak"
        >
          <woot-tabs :index="0" class="sticky top-0 px-4 bg-n-surface-1">
            <woot-tabs-item
              :index="0"
              :name="t('CONTACTS_LAYOUT.SIDEBAR.TABS.DETAILS')"
              :show-badge="false"
              is-compact
            />
          </woot-tabs>
          <div class="p-4">
            <ContactDetails
              :selected-contact="selectedContact"
              @go-to-contacts-list="goToContactsList"
            />
          </div>
        </aside>
      </div>
    </ContactsDetailsLayout>
  </div>
</template>
